import React, { useEffect, useRef, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { getter } from '@progress/kendo-data-query';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { debounce } from 'lodash';
import AddControlsObservationDialog from './dialogs/add-controls-observation-dialog';
import styles from './style.module.scss';
import ApiGrid, { ApiGridHandle } from '@components-clean/core/api-grid';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import CoreSplitter from '@components-clean/core/core-splitter';
import { GridHeaderTitle, RowMenuAction } from '@components-clean/core/grid/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { ActionsEmailPreviewDto, ApiGridParams, ObservationRecord } from '@interfaces/central-monitoring';
import { Action } from '@interfaces-clean/actions.interface';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import {
    AutomationStatusSetupProps,
    ChipHeaders,
    EnumConverter,
    FilterContext,
    MonitoringToolKitTables,
    Observation,
    ObservationDto,
    ObservationProps,
    ObservationType,
    SelectedRowsProps,
} from '@interfaces-clean/observations.interface';
import { ControlsReviewLog } from '@interfaces-clean/review-logs.interface';
// TODO - update these dialog imports to use the correct dialogs
import { AutomationTypes } from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import AddActionDialog, {
    AddActionForm,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/add-action-dialog';
import DeleteActionDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/delete-action-dialog';
import EditActionDialog, {
    EditActionForm,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/edit-action-dialog';
import EmailControlsActionsPreview from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/email-controls-actions-dialog';
import AddReviewLogDialog, {
    AddReviewLog,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/add-review-log-dialog';
import EditControlsReviewLogDialog, {
    EditReviewLogForm,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/controls/review-logs/edit-controls-review-log-dialog';
import DeleteControlsReviewLogDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/delete-controls-review-log-dialog';
import DeleteObservationDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/delete-observation-dialog';
import EditControlsObservationDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/edit-controls-observation-dialog';
import { checkFiltersApplied } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/helpers/filter-helper';
import {
    EditForm,
    ObservationInputs,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation.interface';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { useBoolean, useTranslation } from '@hooks/index';
import { GridFullView, GridViewOptions, SplitterOrientation } from '@utils/constants';
import {
    stringDateWithoutTimeFromLocal,
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import { ShowError, ShowMessage } from '@utils/notification';
import ActionsApi from '@apis/bff-api/central-monitoring/actions';
import ControlsReviewLogsApi from '@apis/bff-api/central-monitoring/controls-review-log';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

// NOTE - this is a copy of the Study Observations version of the code
//
// Even though we haven't done most of the stories yet I've left all the original code in there so we don't have to work
// out what to add in later stories. I've updated things from Study to Controls in as many places as I can.
//
// I've pointed the add observation dialog to a new empty dialog since that is definitely different but left the other
// dialogs for now (some might be the same) because add observation is the only one you can get to in the current UI.

type SelectionState = {
    recordId?: string;
    reviewLogRecordId?: string;
    selectedItemsCount: number;
    childGridParams: Extract<ApiGridParams, { grid: 'Observations' | 'Actions' | 'ReviewLog' }>;
    selectedField: string;
    bulkUpdateRows: SelectedRowsProps[];
    selectedChildChipHeader: ChipHeaders;
    childTableName: string;
    childMenuActionItems: RowMenuAction[] | undefined;
    selectedChildGridTitle: GridHeaderTitle;
    selectedItemId?: string | null;
    actionRecordId?: string;
};

const getExportChildFileName = (
    grid: ApiGridParams['grid'],
    studyNumber: string,
    selectedChildChipHeader: ChipHeaders,
    selectedParentChipHeader: ChipHeaders,
    observationRecordId?: string,
    reviewLogRecordId?: string,
    actionRecordId?: string,
): string => {
    const utcNow = stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy');
    switch (grid) {
        case 'Observations':
            return `Controls Observation ID ${observationRecordId}_${studyNumber}_${utcNow}`;
        case 'Actions':
            if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                return `Actions for Review Log ID ${reviewLogRecordId}_${studyNumber}_${utcNow}`;
            }

            return `Actions for Controls Obs ID ${observationRecordId}_${studyNumber}_${utcNow}`;
        case 'ReviewLog':
            if (selectedChildChipHeader === ChipHeaders.ReviewLogHistory) {
                return `Review Log History for Controls Observation ID ${observationRecordId}_${studyNumber}_${utcNow}`;
            }
            if (selectedParentChipHeader === ChipHeaders.ActionView) {
                return `Controls Review Logs_${studyNumber}_Action ID ${actionRecordId}_${utcNow}`;
            }
            return `Controls Review Logs_${studyNumber}_Observation ID ${observationRecordId}_${utcNow}`;
        default:
            return `Controls Observation ID ${observationRecordId}_${studyNumber}_${utcNow}`;
    }
};

const ControlsObservations = ({ orgKey, studyRef, studyNumber, canEdit, isHalfScreen }: ObservationProps) => {
    const showAddDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showBulkUpdateDialog = useBoolean(false);
    const [observation, setObservation] = useState<Observation>();
    const showDeleteDialog = useBoolean(false);
    const showAddActionDialog = useBoolean(false);
    const showEditActionDialog = useBoolean(false);
    const showActionDeleteDialog = useBoolean(false);
    const showEditReviewLogDialog = useBoolean(false);
    const showDeleteReviewLogDialog = useBoolean(false);
    const showBulkUpdateWarningDialog = useBoolean(false);
    const isChildGridSelection = useBoolean(false);
    const showAddReviewLogDialog = useBoolean(false);

    const [updateParentGridParams, setParentGridParams] = useState<ApiGridParams>({
        grid: 'Observations',
        studyRef,
        level: ObservationType.Controls,
        observationId: null,
        isChild: false,
    });
    const [bulkUpdateFields, setBulkUpdateFields] = useState<SelectedFieldValuesProps[]>([]);
    const { trigger: saveObservation } = ObservationsApi.useSave(studyRef);
    const { trigger: updateObservation } = ObservationsApi.useUpdate(studyRef);
    const { trigger: saveAction } = ActionsApi.useSave(studyRef);
    const { trigger: updateAction } = ActionsApi.useUpdate(studyRef);
    const { trigger: updateReviewLog } = ControlsReviewLogsApi.useUpdate(studyRef);
    const { trigger: bulkUpdateObservations } = ObservationsApi.useBulkUpdate(studyRef);
    const { trigger: bulkUpdateReviewLogs } = ControlsReviewLogsApi.useBulkUpdate(studyRef);
    const { trigger: bulkUpdateActions } = ActionsApi.useBulkUpdate(studyRef);
    const { trigger: saveReviewLog } = ControlsReviewLogsApi.useSave(studyRef);
    const { trigger: HasClosedBeforeLatestReview } = ObservationsApi.useHasClosedBeforeLatestReview(studyRef);

    const actionsCount = useRef<number>(0);
    const reviewLogsCount = useRef<number>(0);
    const [selectedParentChipHeader, setParentSelectedChipHeader] = useState<ChipHeaders>(ChipHeaders.ObservationsView);

    const parentGridRef = useRef<ApiGridHandle>(null);
    const childGridRef = useRef<ApiGridHandle>(null);

    const {
        trigger: addAndReviewDialog,
        data: reviewLogData,
        isMutating: loadingReviewLog,
    } = ControlsReviewLogsApi.useGetMutation(studyRef);
    const handleReview = async (reviewLogId: string) => {
        await addAndReviewDialog(reviewLogId);
    };

    const { t } = useTranslation(['common', 'cm']);
    const [parentRecordSelectMessage, setParentRecordSelectMessage] = useState<string>(
        t('cm:observationsGrid:selectObservationInfo'),
    );
    const [parentMultipleRecordSelectMessage, setParentMultipleRecordSelectMessage] = useState<string>(
        t('cm:observationsGrid:multipleSelectObservationInfo'),
    );

    const onAddClose = () => {
        showAddDialog.setFalse();
    };
    const onDeleteClose = () => {
        showDeleteDialog.setFalse();
    };
    const onEditClose = () => {
        showEditDialog.setFalse();
    };

    const [tableName, setTableName] = useState<string>(MonitoringToolKitTables.Observations);
    const [action, setAction] = useState<Action | null>();
    const [reviewLog, setReviewLog] = useState<ControlsReviewLog | null>();
    const [controlsReviewLog, setControlsReviewLog] = useState<ControlsReviewLog | null>();

    const showFilterAppliedDialog = useBoolean(false);
    const parentFilter = useRef<CompositeFilterDescriptor | null>(null);
    const childFilter = useRef<CompositeFilterDescriptor | null>(null);
    const parentChipRef = useRef<ChipHeaders>(ChipHeaders.ObservationsView);
    const childChipRef = useRef<ChipHeaders>(ChipHeaders.ReviewLogView);

    // controls automation status for study
    const automationStatusSetup: AutomationStatusSetupProps = {
        orgKey,
        studyRef,
        automationType: AutomationTypes.Controls,
    };

    useEffect(() => {
        if (reviewLogData && reviewLogData.data && !loadingReviewLog) {
            const review = reviewLogData.data[0] as ControlsReviewLog;
            setReviewLog(review);
            showEditReviewLogDialog.setTrue();
        }
    }, [reviewLogData, loadingReviewLog]);

    const reviewLogMenuActionItems: RowMenuAction[] = [
        {
            id: 1,
            label: t('cm:reviewLogGrid.editReviewLog'),
            onClick: (dataItem) => {
                const review = dataItem as ControlsReviewLog;
                setReviewLog(review);
                showEditReviewLogDialog.setTrue();
            },
            icon: ['far', 'pen-to-square'],
            disabled: !canEdit,
        },
        {
            id: 2,
            label: t('cm:reviewLogGrid.deleteReviewLog'),
            onClick: (dataItem) => {
                const log = dataItem as ControlsReviewLog;
                setControlsReviewLog(log);
                showDeleteReviewLogDialog.setTrue();
            },
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
            disabled: !canEdit,
            rowDisabled: (dataItem: unknown) => {
                const item = dataItem as ControlsReviewLog;
                return item.type !== 'Manual' || item?.is_latest_dataset_date !== 'Yes';
            },
            showMenuItem: (dataItem: unknown) => {
                const item = dataItem as ControlsReviewLog;
                return item.type === 'Manual' && item.is_first_log === 'No';
            },
        },
    ];
    const [selectionState, setSelectionState] = useState<SelectionState>({
        selectedItemsCount: 0,
        childGridParams: {
            grid: 'ReviewLog',
            studyRef,
            level: ObservationType.Controls,
            observationId: null,
            reviewLogId: null,
            isChild: true,
            isHistory: false,
        },
        selectedField: '',
        bulkUpdateRows: [],
        selectedChildChipHeader: ChipHeaders.ReviewLogView,
        childTableName: MonitoringToolKitTables.ControlsReviewLogs,
        childMenuActionItems: reviewLogMenuActionItems,
        selectedChildGridTitle: {
            id: ChipHeaders.ReviewLogView,
            label: 'Review Logs',
        },
    });

    const handleDelete = (success: boolean | undefined) => {
        if (success) {
            // Refresh Parent Review Log grid / Actions grid, as the child Observation could have many Review Logs / Actions
            // which will be deleted
            // We might need to keep filters
            if (
                selectedParentChipHeader === ChipHeaders.ReviewLogView ||
                selectedParentChipHeader === ChipHeaders.ActionView
            )
                parentGridRef?.current?.refreshData(false, true);
            else parentGridRef?.current?.removeRow(observation?.id as string, false);
            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            setSelectionState((old) => ({
                ...old,
                selectedField: '',
            }));
        } else {
            ShowError(t('common:errorMessage'));
        }
        showDeleteDialog.setFalse();
    };
    const onSubmit = async (data: ObservationInputs) => {
        try {
            const response = await saveObservation({
                datasetOpenedOn: data.datasetOpenedOn?.id,
                associationType: EnumConverter(data.associationType),
                associationRef: data.associationType?.id as string,
                panelRef: data.panel?.reference,
                observationDate: stringDateWithoutTimeFromLocal(data.observationDate!)!,
                isSystemGenerated: false,
                type: ObservationType.Controls,
                ownerRef: data.owner?.ref,
                observation: data.observation,
                rootCause: data.rootCause,
                comments: data.comments,
                isScatterPlot: false,
                closedDate: data.closedDate ? stringDateWithoutTimeFromLocal(data.closedDate) : undefined,
                timePeriod: data.timePeriod ? stringDateWithoutTimeFromLocal(data.timePeriod) : undefined,
            });

            showAddDialog.setFalse();
            if (response) {
                parentGridRef?.current?.addRowAndSelect(response.id!.toString());
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
            showAddDialog.setFalse();
        }
    };

    const onSubmitEdit = async (data: EditForm) => {
        try {
            const response = await updateObservation({
                closedDate: data.closedDate ? stringDateWithoutTimeFromLocal(data.closedDate) : undefined,
                observationDate: stringDateWithoutTimeFromLocal(data.observationDate!)!,
                observation: data.observation,
                comments: data.comments,
                rootCause: data.rootCause,
                ownerRef: data.owner?.ref,
                ownerName: data.owner?.fullName,
                observationId: observation?.id as unknown as number | undefined,
            });

            if (response?.success) {
                if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                    parentGridRef?.current?.refreshRow(response.id!.toString());
                }
                if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                }
                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            showEditDialog.setFalse();
        }
    };

    const handleActionDelete = (success: boolean | undefined) => {
        if (success) {
            if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                if (action) {
                    parentGridRef?.current?.refreshRow(action.observation_id);
                }
                childGridRef?.current?.removeRow(action?.id as string, false);
            } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                childGridRef?.current?.removeRow(action?.id as string, false);
                // This should be true since we're looking at associated actions of this review log
                if (action?.review_log_id != null) parentGridRef?.current?.refreshRow(action?.review_log_id);
            } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                parentGridRef?.current?.removeRow(action?.id as string, false);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                }));
            }
            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
        } else {
            ShowError(t('common:errorMessage'));
        }
        showActionDeleteDialog.setFalse();
    };

    const editReviewLog = async (data: EditReviewLogForm) => {
        try {
            const response = await updateReviewLog({
                reviewLogDataId: Number(reviewLog?.id),
                comments: data.comments,
                decision: data.decision?.name,
                decisionId: data.decision?.id,
                observationId: Number(reviewLog?.observation_id),
            });

            if (response.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });

                if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                    parentGridRef?.current?.refreshRow(response.id!.toString());

                    if (selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogHistory)
                        childGridRef?.current?.refreshRow(response.id!.toString());
                }

                if (selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView) {
                    if (selectedParentChipHeader === ChipHeaders.ActionView) {
                        childGridRef?.current?.refreshData();
                    } else {
                        parentGridRef?.current?.refreshRow(reviewLog?.observation_id as unknown as string);
                        childGridRef?.current?.refreshRow(response.id!.toString());
                    }
                } else if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                }
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch (ex) {
            ShowError(t('common:errorMessage'));
            window.console.error(ex);
        } finally {
            showEditReviewLogDialog.setFalse();
        }
    };

    const onSubmitBulkUpdate = async (data: SelectedFieldValuesProps[]) => {
        try {
            let clearClosedDate = false;
            let clearCompletedDate = false;
            let clearDueDate = false;
            let clearRequestedDate = false;
            let refreshObs = false;

            const getFieldValue = (field: string, dropDownName?: boolean) => {
                const fieldObj = data.find((f) => f.field === field);
                if (fieldObj) {
                    if (fieldObj.type === 'date' && fieldObj.value !== '' && fieldObj.value != null) {
                        return assumeUtc(new Date(fieldObj.value));
                    }
                    if (fieldObj.type === 'date' && fieldObj.value === '') {
                        switch (field) {
                            case 'closedDate':
                                clearClosedDate = true;
                                break;
                            case 'completedDate':
                                clearCompletedDate = true;
                                break;
                            case 'dueDate':
                                clearDueDate = true;
                                break;
                            case 'requestedDate':
                                clearRequestedDate = true;
                                break;
                            default:
                                break;
                        }

                        return null;
                    }
                    if (fieldObj.type === 'controlsDecisionLov' && dropDownName === true && fieldObj.text !== '') {
                        return fieldObj.text;
                    }
                    return fieldObj.value;
                }

                return null;
            };

            let response: boolean = false;
            if (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Observations) {
                const closedDateValue = getFieldValue('closedDate')?.toString();

                response = await bulkUpdateObservations({
                    observationIds: selectionState.bulkUpdateRows.map((item) => item.id),
                    closedDate: closedDateValue ? stringDateWithoutTimeFromLocal(closedDateValue) : undefined,
                    ownerRef: getFieldValue('ownerRef'),
                    comments: getFieldValue('comments'),
                    rootCause: getFieldValue('rootCause'),
                    clearClosedDate,
                });
            } else if (
                (!isChildGridSelection.value && tableName === MonitoringToolKitTables.ControlsReviewLogs) ||
                (isChildGridSelection.value &&
                    selectionState.childTableName === MonitoringToolKitTables.ControlsReviewLogs)
            ) {
                const rawValue = getFieldValue('decisionId');
                response = await bulkUpdateReviewLogs({
                    reviewLogIds: selectionState.bulkUpdateRows.map((item) => item.id),
                    decisionId: rawValue === null ? null : Number(rawValue),
                    decision: getFieldValue('decisionId', true),
                    comments: getFieldValue('comments'),
                });

                refreshObs = data.some((x) => x.field === 'decisionId');
            } else if (
                (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                (isChildGridSelection.value && selectionState.childTableName === MonitoringToolKitTables.Actions)
            ) {
                const completedDateValue = getFieldValue('completedDate')?.toString();
                const dueDateValue = getFieldValue('dueDate')?.toString();
                const requestedDateValue = getFieldValue('requestedDate')?.toString();

                response = await bulkUpdateActions({
                    actionIds: selectionState.bulkUpdateRows.map((item) => item.id),
                    completedDate: completedDateValue ? stringDateWithoutTimeFromLocal(completedDateValue) : undefined,
                    dueDate: dueDateValue ? stringDateWithoutTimeFromLocal(dueDateValue) : undefined,
                    ownerRef: getFieldValue('ownerRef'),
                    requestedDate: stringDateWithoutTimeFromLocal(requestedDateValue!)!,
                    resolution: getFieldValue('resolution'),
                    studyFunctionRef: getFieldValue('responsibleFunction'),
                    clearCompletedDate,
                    clearDueDate,
                    clearRequestedDate,
                });

                refreshObs = data.some((x) => x.field === 'completedDate');
            }

            if (response) {
                if (!isChildGridSelection.value) {
                    parentGridRef?.current?.refreshData(false);
                    if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView && refreshObs)
                        childGridRef?.current?.refreshData(false);
                } else {
                    childGridRef?.current?.refreshData(false);
                    if (selectedParentChipHeader === ChipHeaders.ObservationsView && refreshObs)
                        parentGridRef?.current?.refreshData(false);
                }
                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            showBulkUpdateDialog.setFalse();
        }
    };

    const validateBulkUpdate = async (data: SelectedFieldValuesProps[]) => {
        if (tableName === MonitoringToolKitTables.Observations) {
            // Only date field is closed date
            const closedDateUpdated = data.some((item) => item.type === 'date');
            if (closedDateUpdated) {
                const ids = selectionState.bulkUpdateRows.map((item) => item.id);
                const response = await HasClosedBeforeLatestReview(ids);
                if (response) {
                    setBulkUpdateFields(data);
                    showBulkUpdateWarningDialog.setTrue();
                    return;
                }
            }
        }

        await onSubmitBulkUpdate(data);
    };

    const handleReviewLogDelete = (success: boolean | undefined) => {
        if (success) {
            if (controlsReviewLog) {
                // Refresh parent and child grids based on the selected chip headers
                if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                    parentGridRef?.current?.refreshRow(controlsReviewLog.observation_id);
                } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                    parentGridRef?.current?.refreshData(false);
                    parentGridRef?.current?.unselectRow(controlsReviewLog.id);
                    setSelectionState((old) => ({
                        ...old,
                        selectedField: '',
                    }));
                } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                    parentGridRef?.current?.refreshData(false);
                }

                if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                } else if (
                    selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView ||
                    selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogHistory
                ) {
                    // Use refreshData instead of remove row, as menu actions need re-validating
                    childGridRef?.current?.refreshData(false);
                    childGridRef?.current?.unselectRow(controlsReviewLog.id);
                }
            }

            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
        } else {
            ShowError(t('common:errorMessage'));
        }
        showDeleteReviewLogDialog.setFalse();
    };

    const isObservation = (
        selectedItem: ObservationRecord | Action | ControlsReviewLog,
    ): selectedItem is ObservationRecord => {
        return 'open_actions' in selectedItem;
    };

    const onSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        isChildGridSelection.setFalse();
        let selectedRowCount = 0;
        let selectedItem: ObservationRecord | Action | ControlsReviewLog;
        let selectedReviewLogId: string | null = null;

        let selectedItemsProps: SelectedRowsProps[] = [];
        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';

            const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
            selectedRowCount = selectedRows.length;
            selectedItemsProps = selectedRows.map((item: ObservationRecord) => ({
                id: idGetter(item) as number,
                hasOpenActions: item.open_actions > 0,
            }));
            selectedItem = event.dataItems.find((x) => selectedState[idGetter(x)] === true) as unknown as
                | ObservationRecord
                | Action
                | ControlsReviewLog;

            // Only need the review log id when the parent grid displays review logs
            if (selectedParentChipHeader === ChipHeaders.ReviewLogView && event.startDataItem) {
                selectedReviewLogId = event.startDataItem.id;
            }
        } else {
            selectedItem = event.startDataItem as ObservationRecord | Action | ControlsReviewLog;
        }

        if (!selectedItem) {
            setSelectionState((old) => ({
                ...old,
                selectedField: '',
                selectedItemsCount: 0,
                selectedItemId: null,
                bulkUpdateRows: selectedItemsProps,
            }));
            return;
        }

        let selectedObservationId = '';
        let recordID: string | undefined;
        let reviewLogRecordID: string | undefined;
        let actionRecordId: string | undefined;
        if (tableName === MonitoringToolKitTables.Observations) {
            selectedObservationId = selectedItem.id as unknown as string;
            recordID = selectedItem.record_id;
        } else if (
            tableName === MonitoringToolKitTables.Actions ||
            tableName === MonitoringToolKitTables.ControlsReviewLogs
        ) {
            selectedObservationId = selectedItem.observation_id as unknown as string;
            if (!isObservation(selectedItem) && selectedItem.observation_record_id)
                recordID = selectedItem.observation_record_id;
            reviewLogRecordID = selectedItem.record_id;
            actionRecordId = selectedItem.record_id;
        }

        if (isObservation(selectedItem)) {
            actionsCount.current = selectedItem.open_actions;
            reviewLogsCount.current = selectedItem.reviews;
        }

        let selectedActionId = '';
        if (tableName === MonitoringToolKitTables.Actions) {
            const selectedAction = selectedItem as Action;
            selectedActionId = selectedItem.id || '';
            if (selectedAction.review_log_id) selectedReviewLogId = selectedAction.review_log_id;
        }

        setSelectionState((old) => ({
            ...old,
            recordId: recordID,
            reviewLogRecordId: reviewLogRecordID,
            actionRecordId,
            selectedItemsCount: selectedRowCount,
            // Set if only one row selected
            selectedItemId: selectedRowCount === 1 ? selectedItem.id : null,
            childGridParams: {
                ...old.childGridParams,
                studyRef,
                observationId: selectedObservationId,
                actionId: selectedActionId,
                reviewLogId: selectedReviewLogId,
                isChild: true,
                isScatterPlot: false,
                isRateGroup: false,
                isHistory: false,
                level: ObservationType.Controls,
            },
            selectedField: selectedObservationId,
            bulkUpdateRows: selectedItemsProps,
        }));
    };

    const onChildSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        isChildGridSelection.setTrue();

        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';

            const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
            const selectedItemsProps: SelectedRowsProps[] = selectedRows.map((item: ObservationRecord) => ({
                id: idGetter(item) as number,
                hasOpenActions: item.open_actions > 0,
            }));
            setSelectionState((old) => ({
                ...old,
                bulkUpdateRows: selectedItemsProps,
            }));
        }
    };

    const [selectedTitle, setSelectedTitle] = useState<GridHeaderTitle | null>({
        id: ChipHeaders.ObservationsView,
        label: 'Observations',
    });

    const [exportFileName, setExportFileName] = useState<string>(
        `Controls Observations_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
    );

    const mainPanelHeaders = [
        {
            id: ChipHeaders.ObservationsView,
            label: 'Observations',
        },
        {
            id: ChipHeaders.ReviewLogView,
            label: 'Review Logs',
        },
        {
            id: ChipHeaders.ActionView,
            label: 'Actions',
        },
    ];
    const [subPanelHeaders, setSubPanelHeaders] = useState([
        { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
        {
            id: ChipHeaders.ActionView,
            label: 'Actions',
        },
    ]);

    const observationMenuActionItems = (isParentGrid: boolean = true): RowMenuAction[] => [
        {
            id: 1,
            label: t('cm:observationsGrid.editObservation'),
            onClick: (dataItem: unknown) => {
                const obs = dataItem as Observation;
                setObservation(obs);
                showEditDialog.setTrue();
            },
            icon: ['far', 'pen-to-square'],
            disabled: !canEdit,
        },
        ...(isParentGrid
            ? [
                  {
                      id: 2,
                      label: t('cm:observationsGrid.addAction'),
                      onClick: (dataItem: unknown) => {
                          const obs = dataItem as Observation;
                          checkFiltersApplied(
                              FilterContext.AddAction,
                              parentChipRef,
                              childChipRef,
                              parentFilter,
                              childFilter,
                              showFilterAppliedDialog,
                          );
                          setObservation(obs);
                          showAddActionDialog.setTrue();
                      },
                      icon: ['far', 'add'] as IconProp,
                      disabled: !canEdit,
                      rowDisabled: (dataItem: unknown) => {
                          const item = dataItem as ObservationDto;
                          return item.status === 'Closed';
                      },
                  },
              ]
            : []),
        ...(isParentGrid
            ? [
                  {
                      id: 3,
                      label: t('cm:observationsGrid.addReviewLog'),
                      rowDisabled: (dataItem: unknown) => {
                          const item = dataItem as Observation;
                          return (
                              item.type !== 'Manual' ||
                              item.status === 'Closed' ||
                              item.review_log_created_for_latest_dataset === 'Yes'
                          );
                      },
                      onClick: (dataItem: unknown) => {
                          const obs = dataItem as Observation;
                          checkFiltersApplied(
                              FilterContext.AddReviewLog,
                              parentChipRef,
                              childChipRef,
                              parentFilter,
                              childFilter,
                              showFilterAppliedDialog,
                          );
                          setObservation(obs);
                          showAddReviewLogDialog.setTrue();
                      },
                      icon: ['far', 'add'] as IconProp,
                      disabled: !canEdit,
                      showMenuItem: (dataItem: unknown) => {
                          const item = dataItem as Observation;
                          return item.type !== 'System';
                      },
                  },
              ]
            : []),
        {
            id: 4,
            label: t('cm:observationsGrid.deleteObservation'),
            onClick: (dataItem: unknown) => {
                const obs = dataItem as Observation;
                setObservation(obs);
                actionsCount.current = obs.open_actions as unknown as number;
                reviewLogsCount.current = obs.reviews as unknown as number;
                setSelectionState((old) => ({
                    ...old,
                    recordId: obs.record_id,
                }));
                showDeleteDialog.setTrue();
            },
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
            disabled: !canEdit,
            rowDisabled: (dataItem: unknown) => {
                const item = dataItem as Observation;
                return item.type !== 'Manual';
            },
            showMenuItem: (dataItem: unknown) => {
                const item = dataItem as Observation;
                return item.type !== 'System';
            },
        },
    ];
    const actionMenuActionItems: RowMenuAction[] = [
        {
            id: 1,
            label: t('cm:actionGrid.editAction'),
            onClick: (dataItem) => {
                const act = dataItem as Action;
                setAction(act);
                showEditActionDialog.setTrue();
            },
            disabled: !canEdit,
            icon: ['far', 'pen-to-square'],
        },
        {
            id: 2,
            label: t('cm:actionGrid.deleteAction'),
            onClick: (dataItem) => {
                const act = dataItem as Action;
                setAction(act);
                showActionDeleteDialog.setTrue();
            },
            disabled: !canEdit,
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
        },
    ];
    const observationFields = [
        {
            field: 'closedDate',
            type: 'date',
            required: false,
        },
        {
            field: 'comments',
            type: 'text',
            required: false,
        },
        {
            field: 'ownerRef',
            type: 'ownerLov',
            required: false,
        },
        {
            field: 'rootCause',
            type: 'text',
            required: false,
        },
    ];
    const actionFields = [
        {
            field: 'completedDate',
            type: 'date',
            required: false,
        },
        {
            field: 'dueDate',
            type: 'date',
            required: false,
        },
        {
            field: 'ownerRef',
            type: 'ownerLov',
            required: false,
        },
        {
            field: 'requestedDate',
            type: 'date',
            required: true,
        },
        {
            field: 'resolution',
            type: 'text',
            required: false,
        },
        {
            field: 'responsibleFunction',
            type: 'responsibleFunctionLov',
            required: false,
        },
    ];

    const ReviewLogFields = [
        {
            field: 'comments',
            type: 'text',
            required: false,
        },
        {
            field: 'decisionId',
            type: 'controlsDecisionLov',
            required: false,
        },
    ];

    const handleOnBulkUpdateClicked = () => {
        if (selectionState.bulkUpdateRows.length > 0 && !isChildGridSelection.value) {
            showBulkUpdateDialog.setTrue();
        }
    };

    const handleOnChildUpdateClicked = () => {
        if (selectionState.bulkUpdateRows.length > 0 && isChildGridSelection.value) {
            showBulkUpdateDialog.setTrue();
        }
    };

    const [parentMenuActionItems, setParentMenuActionItems] = useState<RowMenuAction[]>(observationMenuActionItems);

    const onParentChipSelected = (chipHeader: number): void => {
        switch (chipHeader) {
            case 1:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogView,
                    childMenuActionItems: reviewLogMenuActionItems,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                    bulkUpdateRows: [],
                }));
                setParentMenuActionItems(observationMenuActionItems(true));
                setExportFileName(
                    `Controls Observations_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
                );
                setSelectedTitle({ id: ChipHeaders.ObservationsView, label: 'Observations' });
                setParentSelectedChipHeader(ChipHeaders.ObservationsView);
                parentChipRef.current = ChipHeaders.ObservationsView;
                childChipRef.current = ChipHeaders.ReviewLogView;
                setTableName(MonitoringToolKitTables.Observations);
                setParentRecordSelectMessage(t('cm:observationsGrid:selectObservationInfo'));
                setParentMultipleRecordSelectMessage(t('cm:observationsGrid:multipleSelectObservationInfo'));
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'Observations',
                    level: ObservationType.Controls,
                    observationId: null,
                    isChild: false,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                    {
                        id: ChipHeaders.ActionView,
                        label: 'Actions',
                    },
                ]);

                break;
            case 2:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    bulkUpdateRows: [],
                }));
                setExportFileName(
                    `Controls Review Logs_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
                );
                setSelectedTitle({ id: ChipHeaders.ReviewLogView, label: 'ReviewLog' });
                setParentSelectedChipHeader(ChipHeaders.ReviewLogView);
                parentChipRef.current = ChipHeaders.ReviewLogView;
                childChipRef.current = ChipHeaders.ObservationsView;
                setTableName(MonitoringToolKitTables.ControlsReviewLogs);
                setParentRecordSelectMessage(t('cm:reviewLogGrid:selectReviewLogInfo'));
                setParentMultipleRecordSelectMessage(t('cm:reviewLogGrid:multipleSelectReviewLogsInfo'));
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'ReviewLog',
                    level: ObservationType.Controls,
                    observationId: null,
                    reviewLogId: null,
                    isChild: false,
                    isHistory: false,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    {
                        id: ChipHeaders.ActionView,
                        label: 'Actions',
                    },
                    {
                        id: ChipHeaders.ReviewLogHistory,
                        label: 'Review Log History',
                    },
                ]);
                setParentMenuActionItems(reviewLogMenuActionItems);
                break;
            case 3:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        actionId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    bulkUpdateRows: [],
                }));
                setExportFileName(
                    `Controls Actions_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
                );
                setSelectedTitle({ id: ChipHeaders.ActionView, label: 'Actions' });
                setParentSelectedChipHeader(ChipHeaders.ActionView);
                parentChipRef.current = ChipHeaders.ActionView;
                childChipRef.current = ChipHeaders.ObservationsView;
                setParentMenuActionItems(actionMenuActionItems);
                setTableName(MonitoringToolKitTables.Actions);
                setParentRecordSelectMessage(t('cm:actionGrid:selectActionInfo'));
                setParentMultipleRecordSelectMessage(t('cm:actionGrid:multipleSelectActionsInfo'));
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'Actions',
                    level: ObservationType.Controls,
                    observationId: null,
                    reviewLogId: null,
                    isChild: false,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    {
                        id: ChipHeaders.ReviewLogView,
                        label: 'Review Logs',
                    },
                ]);
                break;
            default:
                setSelectedTitle({ id: ChipHeaders.ObservationsView, label: 'Observations' });
                setParentSelectedChipHeader(ChipHeaders.ObservationsView);
                parentChipRef.current = ChipHeaders.ObservationsView;
                setParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'Observations',
                    level: ObservationType.Controls,
                    observationId: null,
                    isChild: false,
                    reviewLogId: null,
                }));
        }
    };
    const onChildChipSelected = (chipHeader: number): void => {
        switch (chipHeader) {
            case 1:
                childChipRef.current = ChipHeaders.ObservationsView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        reviewLogId: 'reviewLogId' in old.childGridParams ? old.childGridParams.reviewLogId : null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                }));
                break;
            case 2:
                childChipRef.current = ChipHeaders.ReviewLogView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        reviewLogId: 'reviewLogId' in old.childGridParams ? old.childGridParams.reviewLogId : null,
                        isChild: true,
                        actionChild: selectedParentChipHeader === ChipHeaders.ActionView,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogView,
                    childTableName: MonitoringToolKitTables.ControlsReviewLogs,
                    childMenuActionItems: reviewLogMenuActionItems,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                }));
                break;

            case 3:
                childChipRef.current = ChipHeaders.ActionView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Actions',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        // Only for Review Log parent grid showing Actions child grid do we want to use the
                        // selected review log id as the filter for the actions. Everything else uses the observation
                        // id for the filter.
                        reviewLogId:
                            selectedParentChipHeader === ChipHeaders.ReviewLogView ? old.selectedItemId || null : null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ActionView,
                    childTableName: MonitoringToolKitTables.Actions,
                    childMenuActionItems: actionMenuActionItems,
                    selectedChildGridTitle: { id: ChipHeaders.ActionView, label: 'Actions' },
                }));
                break;
            case 4:
                childChipRef.current = ChipHeaders.ReviewLogHistory;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Controls,
                        observationId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                        isHistory: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogHistory,
                    childTableName: MonitoringToolKitTables.ControlsReviewLogs,
                    childMenuActionItems: undefined,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogHistory, label: 'Review Log History' },
                }));
                break;
            default:
                childChipRef.current = ChipHeaders.ObservationsView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Controls,
                        observationId: null,
                        reviewLogId: null,
                        isChild: true,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                }));
        }
    };
    useEffect(() => {
        setExportFileName(
            `Controls Observations_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
        );
    }, [studyNumber]);

    const addAction = async (data: AddActionForm) => {
        if (selectionState.selectedChildChipHeader !== ChipHeaders.ActionView) {
            onChildChipSelected(ChipHeaders.ActionView);
        }
        try {
            const response = await saveAction({
                observationId: Number(data.observationId),
                ownerRef: data.owner?.ref,
                dueDate: data.dueDate ? stringDateWithoutTimeFromLocal(data.dueDate) : undefined,
                description: data.description,
                comments: data.comments,
                resolution: data.resolution,
                requestedDate: stringDateWithoutTimeFromLocal(data.requestedDate!)!,
                completedDate: data.completedDate ? stringDateWithoutTimeFromLocal(data.completedDate) : undefined,
                studyFunctionRef: data.responsibleFunction?.ref,
                controlReviewLogId: data.associatedReviewLog?.id,
            });

            if (response?.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
                parentGridRef?.current?.refreshRow(data.observationId!);
                childGridRef?.current?.addRowAndSelect(response.id!.toString());
                showAddActionDialog.setFalse();
            } else {
                ShowError(t('common:errorMessage'));
                showAddActionDialog.setFalse();
            }
        } catch {
            ShowError(t('common:errorMessage'));
            showAddActionDialog.setFalse();
        }
    };

    const editAction = async (data: EditActionForm) => {
        try {
            const response = await updateAction({
                actionId: Number(action?.id),
                dueDate: data.dueDate ? stringDateWithoutTimeFromLocal(data.dueDate) : undefined,
                description: data.description,
                comments: data.comments,
                resolution: data.resolution,
                requestedDate: stringDateWithoutTimeFromLocal(data.requestedDate!)!,
                completedDate: data.completedDate ? stringDateWithoutTimeFromLocal(data.completedDate) : undefined,
                ownerRef: data.owner?.ref,
                studyFunctionRef: data.responsibleFunction?.ref,
                controlReviewLogId: data.associatedReviewLog?.id,
            });

            if (response.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
                const newAssociation = data.associatedReviewLog?.datasetDate || null;

                // Handle parent grid actions
                if (selectedParentChipHeader === ChipHeaders.ObservationsView && action) {
                    parentGridRef?.current?.refreshRow(action.observation_id);
                } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                    parentGridRef?.current?.refreshRow(response.id!.toString());
                    // If review log association changes then refresh the child grid
                    // This should either change/remove the review log
                    if (
                        selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView &&
                        action &&
                        action.associated_review_log !== newAssociation
                    ) {
                        childGridRef?.current?.refreshData(false);
                    }
                }

                // Handle child grid actions
                if (selectionState.selectedChildChipHeader === ChipHeaders.ActionView) {
                    if (
                        selectedParentChipHeader === ChipHeaders.ReviewLogView &&
                        action &&
                        action.associated_review_log !== newAssociation
                    ) {
                        // Refresh the parent grid with review logs (multiple rows may need updating)
                        parentGridRef?.current?.refreshData(false);
                        childGridRef?.current?.removeRow(response.id!.toString());
                    } else {
                        childGridRef?.current?.refreshRow(response.id!.toString());
                    }
                } else if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView && action) {
                    childGridRef?.current?.refreshData();
                }

                showEditActionDialog.setFalse();
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            showEditActionDialog.setFalse();
        }
    };

    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '50%', min: '15px' }, { min: '35px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };
    const [gridCurrentView, setGridCurrentView] = useState<GridViewOptions>(SplitterOrientation.vertical);
    const [isReorderMode, setIsReorderMode] = useState(false);
    useEffect(() => {
        setIsReorderMode(false);
        setGridCurrentView(SplitterOrientation.vertical);
        setParentGridParams((prevState) => ({
            ...prevState,
            studyRef,
        }));
    }, [studyRef]);

    const divHeight = isHalfScreen ? 'calc(50vh - 75px)' : 'calc(100vh - 128px)';
    const showEmailActions = useBoolean(false);
    const [emailPreviewData, setEmailPreviewData] = useState<ActionsEmailPreviewDto | null>(null);
    const { trigger: emailValidate, isMutating } = ActionsApi.useCanEmail(studyRef);
    const emailCallback = (selectedState: { [p: string]: boolean | number[] }) => {
        const selectedIds = Object.keys(selectedState).filter((k) => !!selectedState[k]);

        if (!selectedIds.length) return;

        emailValidate(selectedIds)
            .then((resp) => {
                setEmailPreviewData(resp);
                showEmailActions.setTrue();
            })
            .catch((e) => {
                window.console.error(e);
            });
    };

    const addReviewLog = async (data: AddReviewLog) => {
        const result = { success: false, responseId: null as string | null };
        if (selectionState.selectedChildChipHeader !== ChipHeaders.ReviewLogView) {
            onChildChipSelected(ChipHeaders.ReviewLogView);
        }
        try {
            const response = await saveReviewLog({
                observationId: Number(observation?.id),
                datasetId: Number(data.dataset.id),
            });

            if (response?.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });

                result.success = true;
                result.responseId = response.id!.toString();

                parentGridRef?.current?.refreshRow(observation!.id);
                // When virtualized, addRowAndSelect refreshes the gird
                childGridRef?.current?.addRowAndSelect(result.responseId);

                if (data.addAndReview) {
                    await handleReview(result.responseId);
                }
            } else {
                result.success = false;
                ShowError(t('common:errorMessage'));
            }
        } catch {
            result.success = false;
            ShowError(t('common:errorMessage'));
        } finally {
            showAddReviewLogDialog.setFalse();
        }
    };

    const childGridFilterable = () => {
        if (
            selectedParentChipHeader === ChipHeaders.ActionView &&
            selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView
        )
            return false;

        return selectionState.selectedChildChipHeader !== ChipHeaders.ObservationsView;
    };

    return (
        <div style={{ height: divHeight }}>
            {showEmailActions.value && (
                <EmailControlsActionsPreview
                    studyRef={studyRef}
                    onClose={() => {
                        showEmailActions.setFalse();
                    }}
                    previewData={emailPreviewData}
                />
            )}
            <CoreSplitter
                className="!k-h-full"
                orientation={SplitterOrientation.vertical}
                data-testid="controls-obs-grid-splitter"
                alignmentValues={
                    gridCurrentView === GridFullView.full || isReorderMode
                        ? [
                              {
                                  size: '50%',
                              },
                              { size: '50%', collapsed: true },
                          ]
                        : panes
                }
                onChange={onPaneChange}
            >
                <div data-testid="observations-list-pane" className="k-h-full">
                    {studyRef && (
                        <ApiGrid
                            selectedTitle={selectedTitle}
                            rowMenuActions={parentMenuActionItems}
                            title={mainPanelHeaders}
                            gridParams={updateParentGridParams}
                            table={tableName}
                            maxHeight="calc(100vh)"
                            testDataId="controls-observations-grid"
                            onChipHeaderSelected={onParentChipSelected}
                            selectedChipHeader={selectedParentChipHeader}
                            noPadding
                            addCallback={
                                selectedParentChipHeader === ChipHeaders.ObservationsView
                                    ? showAddDialog.setTrue
                                    : showAddActionDialog.setTrue
                            }
                            canEdit={canEdit}
                            selectable={{ enabled: true, mode: 'multiple', drag: true }}
                            onSelectionChange={onSelectionChange}
                            dataItemKey="id"
                            exportFilename={exportFileName}
                            showSendEmailIcon={tableName === MonitoringToolKitTables.Actions}
                            showBulkUpdateIcon
                            showAddIcon={tableName === MonitoringToolKitTables.Observations}
                            bulkUpdateTooltip={
                                tableName === MonitoringToolKitTables.Observations
                                    ? t('cm:observationsGrid.editObservations')
                                    : tableName === MonitoringToolKitTables.ControlsReviewLogs
                                      ? t('cm:reviewLogGrid.editReviewLogs')
                                      : tableName === MonitoringToolKitTables.Actions
                                        ? t('cm:actionGrid.editActions')
                                        : undefined
                            }
                            addRecordTooltip={
                                tableName === MonitoringToolKitTables.Observations
                                    ? t('cm:observationsGrid.addObservation')
                                    : undefined
                            }
                            showAutomationStatus
                            gridStatusSetup={{
                                automationStatusSetup,
                            }}
                            ref={parentGridRef}
                            filterListener={(newFilterState) => {
                                parentFilter.current = newFilterState || null;
                            }}
                            emailCallback={emailCallback}
                            loading={isMutating && parentChipRef.current === ChipHeaders.ActionView}
                            onBulkUpdateClicked={handleOnBulkUpdateClicked}
                            useFilterWarning={selectionState.bulkUpdateRows.length > 0}
                            scrollable="virtual"
                            showPlaceholder={false}
                        />
                    )}
                </div>
                <div className="k-h-full">
                    {selectionState.selectedField && selectionState.selectedItemsCount === 1 && (
                        <ApiGrid
                            key={selectionState.selectedChildChipHeader + selectionState.selectedField}
                            title={subPanelHeaders}
                            selectedTitle={selectionState.selectedChildGridTitle}
                            gridParams={selectionState.childGridParams}
                            rowMenuActions={selectionState.childMenuActionItems}
                            table={selectionState.childTableName}
                            maxHeight="calc(100vh)"
                            testDataId="controls_observations-review-log-grid"
                            onChipHeaderSelected={onChildChipSelected}
                            selectedChipHeader={selectionState.selectedChildChipHeader}
                            noPadding
                            exportFilename={getExportChildFileName(
                                selectionState.childGridParams.grid,
                                studyNumber,
                                selectionState.selectedChildChipHeader,
                                selectedParentChipHeader,
                                selectionState.recordId,
                                selectionState.reviewLogRecordId,
                                selectionState.actionRecordId,
                            )}
                            canEdit={canEdit}
                            onSelectionChange={onChildSelectionChange}
                            bulkUpdateTooltip={
                                selectionState.childTableName === MonitoringToolKitTables.Observations
                                    ? t('cm:observationsGrid.editObservations')
                                    : selectionState.childTableName === MonitoringToolKitTables.ControlsReviewLogs
                                      ? t('cm:reviewLogGrid.editReviewLogs')
                                      : selectionState.childTableName === MonitoringToolKitTables.Actions
                                        ? t('cm:actionGrid.editActions')
                                        : undefined
                            }
                            showSendEmailIcon={selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView}
                            showBulkUpdateIcon={
                                selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView ||
                                selectionState.selectedChildGridTitle?.id === ChipHeaders.ReviewLogView
                            }
                            showAddIcon={false}
                            filterable={childGridFilterable()}
                            ref={childGridRef}
                            dataItemKey="id"
                            selectable={{ enabled: true, mode: 'multiple', drag: true }}
                            filterListener={(newFilterState) => {
                                childFilter.current = newFilterState || null;
                            }}
                            emailCallback={emailCallback}
                            loading={isMutating && childChipRef.current === ChipHeaders.ActionView}
                            isChildGrid
                            showPlaceholder={false}
                            useFilterWarning={selectionState.bulkUpdateRows.length > 0}
                            onBulkUpdateClicked={handleOnChildUpdateClicked}
                            scrollable="virtual"
                        />
                    )}
                    {selectionState.selectedItemsCount > 1 && (
                        <InfoField type="Info" message={parentMultipleRecordSelectMessage} layout="center" />
                    )}
                    {(!selectionState.selectedField || selectionState.selectedItemsCount === 0) && (
                        <InfoField type="Info" message={parentRecordSelectMessage} layout="center" />
                    )}
                </div>
            </CoreSplitter>
            {showFilterAppliedDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-add-controls-observation"
                    title={t('grid.filterSortWarning.title')}
                    onSubmit={() => {
                        showFilterAppliedDialog.setFalse();
                    }}
                    onClose={() => {
                        showAddDialog.setFalse();
                        showAddActionDialog.setFalse();
                        showEditDialog.setFalse();
                        showEditActionDialog.setFalse();
                        showEditReviewLogDialog.setFalse();
                        showDeleteDialog.setFalse();
                        showActionDeleteDialog.setFalse();
                        showBulkUpdateDialog.setFalse();
                        showFilterAppliedDialog.setFalse();
                        showDeleteReviewLogDialog.setFalse();
                        showAddReviewLogDialog.setFalse();
                    }}
                    primaryBtnLabel={t('common:continue')}
                >
                    {t('grid.filterSortWarning.body')}
                </ConfirmationDialog>
            )}
            {showAddDialog.value && !showFilterAppliedDialog.value && (
                <AddControlsObservationDialog
                    studyRef={studyRef}
                    orgKey={orgKey}
                    onSubmit={onSubmit}
                    onClose={onAddClose}
                />
            )}
            {showEditDialog.value && !showFilterAppliedDialog.value && observation && (
                <EditControlsObservationDialog
                    studyRef={studyRef}
                    onSubmit={onSubmitEdit}
                    onClose={onEditClose}
                    observation={observation}
                />
            )}
            {showAddActionDialog.value && !showFilterAppliedDialog.value && observation && (
                <AddActionDialog
                    observationId={observation.id}
                    studyRef={studyRef}
                    onSubmit={addAction}
                    onClose={showAddActionDialog.setFalse}
                    type={ObservationType.Controls}
                />
            )}
            {showDeleteDialog.value && !showFilterAppliedDialog.value && (
                <DeleteObservationDialog
                    recordId={selectionState.recordId ?? ''}
                    actionsCount={actionsCount.current}
                    reviewLogsCount={reviewLogsCount.current}
                    onClose={onDeleteClose}
                    onDeleteHandler={handleDelete}
                    studyRef={studyRef}
                    observationId={observation?.id as unknown as number}
                    observation={observation?.observation}
                    siteName={observation?.site_name}
                    dataSetDate={stringDateWithoutTimeFromUtc(observation?.dataset_opened_on, 'DD-MMM-YYYY')}
                    type={ObservationType.Controls}
                />
            )}
            {showEditActionDialog.value && !showFilterAppliedDialog.value && action && (
                <EditActionDialog
                    action={action}
                    studyRef={studyRef}
                    onSubmit={editAction}
                    onClose={showEditActionDialog.setFalse}
                    type={ObservationType.Controls}
                />
            )}
            {showEditReviewLogDialog.value && !showFilterAppliedDialog.value && reviewLog && (
                <EditControlsReviewLogDialog
                    reviewLog={reviewLog}
                    studyRef={studyRef}
                    onSubmit={editReviewLog}
                    onClose={showEditReviewLogDialog.setFalse}
                />
            )}
            {showActionDeleteDialog.value && !showFilterAppliedDialog.value && (
                <DeleteActionDialog
                    recordId={action?.record_id ?? ''}
                    onClose={showActionDeleteDialog.setFalse}
                    onDeleteHandler={handleActionDelete}
                    studyRef={studyRef}
                    observationId={action?.observation_id}
                    actionId={action?.id as unknown as number}
                    type={ObservationType.Controls}
                />
            )}
            {showBulkUpdateDialog.value && !showFilterAppliedDialog.value && (
                <BulkUpdateDialog
                    studyRef={studyRef}
                    onClose={() => {
                        showBulkUpdateDialog.setFalse();
                    }}
                    onSubmit={validateBulkUpdate}
                    dialogTitle={
                        (!isChildGridSelection.value && tableName === MonitoringToolKitTables.ControlsReviewLogs) ||
                        (isChildGridSelection.value &&
                            selectionState.childTableName === MonitoringToolKitTables.ControlsReviewLogs)
                            ? t('cm:bulkUpdateDialog.dialogTitle.controlsReviewLogs')
                            : (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                                (isChildGridSelection.value &&
                                    selectionState.childTableName === MonitoringToolKitTables.Actions)
                              ? t('cm:bulkUpdateDialog.dialogTitle.controlsActions')
                              : t('cm:bulkUpdateDialog.dialogTitle.controls')
                    }
                    warningMessage={
                        (!isChildGridSelection.value && tableName === MonitoringToolKitTables.ControlsReviewLogs) ||
                        (isChildGridSelection.value &&
                            selectionState.childTableName === MonitoringToolKitTables.ControlsReviewLogs)
                            ? t('cm:bulkUpdateDialog.warnings.reviewLogs', {
                                  count: selectionState.bulkUpdateRows.length,
                              })
                            : (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                                (isChildGridSelection.value &&
                                    selectionState.childTableName === MonitoringToolKitTables.Actions)
                              ? t('cm:bulkUpdateDialog.warnings.actions', {
                                    count: selectionState.bulkUpdateRows.length,
                                })
                              : t('cm:bulkUpdateDialog.warnings.study', {
                                    count: selectionState.bulkUpdateRows.length,
                                })
                    }
                    fields={
                        (!isChildGridSelection.value && tableName === MonitoringToolKitTables.ControlsReviewLogs) ||
                        (isChildGridSelection.value &&
                            selectionState.childTableName === MonitoringToolKitTables.ControlsReviewLogs)
                            ? ReviewLogFields
                            : (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                                (isChildGridSelection.value &&
                                    selectionState.childTableName === MonitoringToolKitTables.Actions)
                              ? actionFields
                              : observationFields
                    }
                    unsavedChangesArea="central-monitoring-toolkit"
                />
            )}
            {showBulkUpdateWarningDialog.value && (
                <ConfirmationDialog
                    title={t('cm:bulkUpdateDialog.closedSystemObservationWarningTitle')}
                    dataTestId="confirmation-dialog"
                    onClose={() => showBulkUpdateWarningDialog.setFalse()}
                    onSubmit={async () => {
                        showBulkUpdateWarningDialog.setFalse();
                        await onSubmitBulkUpdate(bulkUpdateFields);
                    }}
                    primaryBtnDisabled={false}
                    ignoreReadonlyCtx
                    className="k-z-1010"
                >
                    <div className={styles.warnings}>
                        <div className={styles.bulkUpdate}>
                            <div className={styles.systemObsClosedDateWarning}>
                                <InfoField
                                    type="Warning"
                                    message={
                                        <div className={styles.infoFieldMessage}>
                                            {t('cm:bulkUpdateDialog.closedSystemObservationWarning1')}
                                        </div>
                                    }
                                />
                                <div className={styles.continueMessage}>
                                    {t('cm:bulkUpdateDialog.closedSystemObservationWarningContinue')}
                                </div>
                            </div>
                        </div>
                    </div>
                </ConfirmationDialog>
            )}
            {showDeleteReviewLogDialog.value && !showFilterAppliedDialog.value && (
                <DeleteControlsReviewLogDialog
                    recordId={controlsReviewLog?.record_id ?? ''}
                    reviewLogId={controlsReviewLog?.id as unknown as number}
                    studyRef={studyRef}
                    observationId={controlsReviewLog && controlsReviewLog.observation_id}
                    onClose={showDeleteReviewLogDialog.setFalse}
                    onDeleteHandler={handleReviewLogDelete}
                    reviewLogDatasetDate={controlsReviewLog?.dataset}
                />
            )}
            {showAddReviewLogDialog.value && !showFilterAppliedDialog.value && observation && (
                <AddReviewLogDialog
                    observationId={observation.id}
                    studyRef={studyRef}
                    onSubmit={addReviewLog}
                    onClose={showAddReviewLogDialog.setFalse}
                    level={ObservationType.Controls}
                />
            )}
        </div>
    );
};
export default ControlsObservations;
